import React, { useEffect, useState } from 'react';
import { MapContainer, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import L from 'leaflet';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Menu,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import WarningIcon from '@mui/icons-material/Warning';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LayersIcon from '@mui/icons-material/Layers';
import SendIcon from '@mui/icons-material/Send';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  stateTravelAdvisoryApi,
  mapStateAdvisoryLevel,
  mapStateAdvisoryToRiskLevel,
} from 'services/stateTravelAdvisoryApi';

// Fix for default marker icons in Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Default risk alerts (fallback if API fails)
const defaultRiskAlerts = [
  { country: 'France', riskType: 'Health', severity: 'Medium' },
  { country: 'Egypt', riskType: 'Political', severity: 'High' },
  { country: 'Japan', riskType: 'Travel', severity: 'Low' },
];

// Country risk levels for coloring - matching the design
const countryRiskLevels = {
  // High risk countries (red)
  Russia: 'high',
  Egypt: 'high',
  Sudan: 'high',
  Syria: 'high',
  Iraq: 'high',
  'South Sudan': 'high',
  // Medium risk countries (orange)
  'United States of America': 'medium',
  // Low risk countries (teal)
  Canada: 'low',
  Greenland: 'low',
  Brazil: 'low',
  Australia: 'low',
  // Other countries (default gray/light colors)
  France: 'default',
  Jordan: 'default',
  Iran: 'default',
  'United Kingdom': 'default',
  Japan: 'default',
  Germany: 'default',
  Mexico: 'default',
  'United Arab Emirates': 'default',
  Singapore: 'default',
  Switzerland: 'default',
};

// Country name mapping for better filter matching
const countryNameMap = {
  'united states': ['united states', 'united states of america', 'usa'],
  'united states of america': ['united states', 'united states of america', 'usa'],
  'russian federation': ['russia', 'russian federation'],
  'russia': ['russia', 'russian federation'],
  'united kingdom': ['united kingdom', 'uk', 'great britain', 'britain'],
  'uk': ['united kingdom', 'uk', 'great britain', 'britain'],
  'south korea': ['south korea', 'korea', 'republic of korea'],
  'north korea': ['north korea', 'democratic people\'s republic of korea', 'dprk'],
  'czech republic': ['czech republic', 'czechia'],
  'east timor': ['east timor', 'timor-leste'],
  'myanmar': ['myanmar', 'burma'],
  'ivory coast': ['ivory coast', 'côte d\'ivoire', 'cote d\'ivoire'],
};

function normalizeCountryName(name) {
  const normalized = name.toLowerCase().trim();
  // Check if this country has aliases
  for (const [key, aliases] of Object.entries(countryNameMap)) {
    if (aliases.includes(normalized)) {
      return key;
    }
  }
  return normalized;
}

function CountryLayer({ showLabels, filters, riskLevels }) {
  const map = useMap();
  const geoJsonLayerRef = React.useRef(null);
  const labelMarkersRef = React.useRef([]);

  useEffect(() => {
    if (!map) return;

    // Remove existing layer and labels
    if (geoJsonLayerRef.current) {
      map.removeLayer(geoJsonLayerRef.current);
    }
    labelMarkersRef.current.forEach((marker) => {
      map.removeLayer(marker);
    });
    labelMarkersRef.current = [];

    // Fetch GeoJSON data and color countries based on risk
    fetch(
      'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json',
    )
      .then((response) => response.json())
      .then((geoJsonData) => {
        const markers = [];

        const layer = L.geoJSON(geoJsonData, {
          style: (feature) => {
            const countryName = feature.properties.name;
            const riskLevel =
              riskLevels[countryName] ||
              countryRiskLevels[countryName] ||
              'default';

            // Apply filters - improved matching for country names
            if (filters.country && filters.country !== '') {
              const filterCountry = normalizeCountryName(filters.country);
              const normalizedCountryName = normalizeCountryName(countryName);
              
              // Bidirectional matching: check if either contains the other
              const countryMatch = 
                normalizedCountryName.includes(filterCountry) ||
                filterCountry.includes(normalizedCountryName) ||
                normalizedCountryName === filterCountry ||
                // Also check direct match with original names
                countryName.toLowerCase().includes(filters.country.toLowerCase()) ||
                filters.country.toLowerCase().includes(countryName.toLowerCase());
              
              if (!countryMatch) {
                return { fillOpacity: 0, opacity: 0 };
              }
            }

            let fillColor = '#e5e7eb'; // Default light gray
            let color = 'white';

            switch (riskLevel) {
              case 'high':
                fillColor = '#dc2626'; // Red - Level 4: Do Not Travel (High Alert)
                break;
              case 'reconsider':
                fillColor = '#f59e0b'; // Orange - Level 3: Reconsider Travel
                break;
              case 'medium':
                fillColor = '#eab308'; // Yellow - Level 2: Exercise Increased Caution
                break;
              case 'low':
                fillColor = '#14b8a6'; // Teal - Level 1: Exercise Normal Precautions
                break;
              case 'safe':
                fillColor = '#10b981'; // Green
                break;
              default:
                fillColor = '#e5e7eb'; // Light gray for other countries
            }

            return {
              fillColor,
              weight: 1,
              opacity: 1,
              color,
              dashArray: '3',
              fillOpacity: 0.8,
            };
          },
          onEachFeature: (feature, layer) => {
            const countryName = feature.properties.name;
            const bounds = layer.getBounds();
            const center = bounds.getCenter();

            // Calculate country area to filter very small countries
            const area =
              bounds.getNorth() -
              bounds.getSouth() +
              bounds.getEast() -
              bounds.getWest();

            // Only show the most important/largest countries to avoid jumbling
            const importantCountries = [
              'Russia',
              'Russian Federation',
              'Canada',
              'United States of America',
              'United States',
              'Brazil',
              'Australia',
              'Greenland',
              'China',
              'India',
              'Argentina',
              'Kazakhstan',
              'Algeria',
              'Saudi Arabia',
              'Mexico',
              'Indonesia',
              'Sudan',
              'Libya',
              'Iran',
              'Mongolia',
              'Angola',
              'South Africa',
              'Colombia',
              'Bolivia',
              'Egypt',
              'Nigeria',
              'Venezuela',
              'Pakistan',
              'Mozambique',
              'Turkey',
              'Chile',
              'Myanmar',
              'Afghanistan',
              'Ukraine',
              'Madagascar',
              'Kenya',
              'France',
              'Yemen',
              'Thailand',
              'Spain',
              'Turkmenistan',
              'Cameroon',
              'Sweden',
              'Morocco',
              'Iraq',
              'Zimbabwe',
              'Japan',
              'Germany',
              'Finland',
              'Malaysia',
              'Vietnam',
              'Norway',
              'Poland',
              'Italy',
              'Philippines',
              'Ecuador',
              'New Zealand',
              'United Kingdom',
            ];

            // Check if country is important
            const normalizedName = countryName.trim();
            const isImportantCountry = importantCountries.some(
              (name) =>
                normalizedName === name ||
                normalizedName.toLowerCase().includes(name.toLowerCase()) ||
                name.toLowerCase().includes(normalizedName.toLowerCase()),
            );

            // Check if country should be shown based on filters - improved matching
            const filterCountry = filters.country ? normalizeCountryName(filters.country) : '';
            const normalizedCountryName = normalizeCountryName(countryName);
            const countryMatchesFilter = 
              !filterCountry ||
              filterCountry === '' ||
              normalizedCountryName.includes(filterCountry) ||
              filterCountry.includes(normalizedCountryName) ||
              normalizedCountryName === filterCountry ||
              // Also check direct match with original names
              countryName.toLowerCase().includes(filters.country?.toLowerCase() || '') ||
              filters.country?.toLowerCase().includes(countryName.toLowerCase());
            
            // Show labels for important countries, OR if country matches filter (even if not in important list)
            const shouldShowLabel =
              isImportantCountry || 
              (countryMatchesFilter && filterCountry !== ''); // Show label if it matches filter

            // Show labels only for very large countries to avoid jumbling
            if (shouldShowLabel && area > 0.5) {
              // Larger font for most important countries
              let fontSize = 9;

              // Check if this is one of the major countries that need larger font
              const isMajorCountry =
                countryName === 'Russia' ||
                countryName === 'Russian Federation' ||
                countryName === 'Canada' ||
                countryName === 'United States of America' ||
                countryName === 'United States' ||
                countryName === 'Australia' ||
                countryName === 'China';

              // Increase font size for major countries
              if (isMajorCountry) {
                fontSize = 14; // Larger font for Russia, Canada, USA, Australia, China
              } else {
                // Adjust based on name length for other countries
                if (countryName.length > 20) {
                  fontSize = 7;
                } else if (countryName.length > 15) {
                  fontSize = 8;
                } else if (countryName.length < 8) {
                  fontSize = 9;
                }
              }

              // Calculate icon width based on text length
              const textWidth = Math.max(
                countryName.length * (fontSize * 0.55),
                60,
              );

              // Get English country name (ensure it's in English)
              const displayName = countryName; // GeoJSON already has English names

              // Custom positions for large countries to avoid misplacement
              let labelPosition = center;
              if (
                countryName === 'Russia' ||
                countryName === 'Russian Federation'
              ) {
                labelPosition = [60, 100]; // Central Russia
              } else if (countryName === 'Canada') {
                labelPosition = [60, -100]; // Central Canada
              } else if (
                countryName === 'United States of America' ||
                countryName === 'United States'
              ) {
                labelPosition = [40, -100]; // Central USA
              } else if (countryName === 'Brazil') {
                labelPosition = [-10, -55]; // Central Brazil
              } else if (countryName === 'Australia') {
                labelPosition = [-25, 135]; // Central Australia
              } else if (countryName === 'China') {
                labelPosition = [35, 105]; // Central China
              }

              // Create a label div icon with black color, no overlay
              const labelDiv = L.divIcon({
                className: 'country-label',
                html: `<div style="
                  background: transparent;
                  font-size: ${fontSize}px;
                  font-weight: 700;
                  color: black;
                  white-space: nowrap;
                  pointer-events: none;
                  text-shadow: 2px 2px 4px rgba(255,255,255,1), -1px -1px 2px rgba(255,255,255,1);
                  text-transform: uppercase;
                  letter-spacing: 0.3px;
                  font-family: 'Arial', 'Helvetica', sans-serif;
                  line-height: 1.2;
                ">${displayName.toUpperCase()}</div>`,
                iconSize: [textWidth + 10, fontSize + 5],
                iconAnchor: [(textWidth + 10) / 2, (fontSize + 5) / 2],
              });

              const marker = L.marker(labelPosition, {
                icon: labelDiv,
                interactive: false,
                zIndexOffset: 1000,
              });
              marker.addTo(map);
              markers.push(marker);
            }
          },
        }).addTo(map);

        geoJsonLayerRef.current = layer;
        labelMarkersRef.current = markers;

        // Zoom to filtered country if a filter is applied
        if (filters.country && filters.country !== '') {
          const matchingFeatures = geoJsonData.features.filter((feature) => {
            const countryName = feature.properties.name;
            const filterCountry = normalizeCountryName(filters.country);
            const normalizedCountryName = normalizeCountryName(countryName);
            
            return (
              normalizedCountryName.includes(filterCountry) ||
              filterCountry.includes(normalizedCountryName) ||
              normalizedCountryName === filterCountry ||
              countryName.toLowerCase().includes(filters.country.toLowerCase()) ||
              filters.country.toLowerCase().includes(countryName.toLowerCase())
            );
          });

          if (matchingFeatures.length > 0) {
            // Create a group of bounds for all matching countries
            const group = new L.featureGroup(
              matchingFeatures.map((feature) => L.geoJSON(feature)),
            );
            const bounds = group.getBounds();
            if (bounds.isValid()) {
              map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
            }
          }
        }

        // Add ocean names
        const oceanNames = [
          { name: 'NORTH ATLANTIC OCEAN', lat: 30, lng: -40 },
          { name: 'SOUTH ATLANTIC OCEAN', lat: -30, lng: -20 },
          { name: 'PACIFIC OCEAN', lat: 0, lng: -150 },
          { name: 'NORTH PACIFIC OCEAN', lat: 20, lng: -160 },
          { name: 'SOUTH PACIFIC OCEAN', lat: -20, lng: -140 },
          { name: 'INDIAN OCEAN', lat: -20, lng: 70 },
          { name: 'ARCTIC OCEAN', lat: 80, lng: 0 },
          { name: 'SOUTHERN OCEAN', lat: -60, lng: 0 },
        ];

        oceanNames.forEach((ocean) => {
          const oceanLabel = L.divIcon({
            className: 'ocean-label',
            html: `<div style="
              background: transparent;
              padding: 2px 4px;
              font-size: 10px;
              font-weight: 600;
              color: #1e40af;
              white-space: nowrap;
              pointer-events: none;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.9);
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-family: 'Arial', 'Helvetica', sans-serif;
              line-height: 1.2;
            ">${ocean.name}</div>`,
            iconSize: [ocean.name.length * 6, 16],
            iconAnchor: [(ocean.name.length * 6) / 2, 8],
          });

          const oceanMarker = L.marker([ocean.lat, ocean.lng], {
            icon: oceanLabel,
            interactive: false,
            zIndexOffset: 500,
          });
          oceanMarker.addTo(map);
          markers.push(oceanMarker);
        });
      })
      .catch((error) => console.error('Error loading GeoJSON data:', error));

    return () => {
      if (geoJsonLayerRef.current) {
        map.removeLayer(geoJsonLayerRef.current);
      }
      labelMarkersRef.current.forEach((marker) => {
        map.removeLayer(marker);
      });
    };
  }, [map, showLabels, filters, riskLevels]);

  return null;
}

function MapControls() {
  const map = useMap();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        display: 'flex',
        gap: 1,
      }}
    >
      <IconButton
        size='small'
        sx={{
          bgcolor: 'white',
          boxShadow: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={() => {
          if (map) {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              map.getContainer().requestFullscreen();
            }
          }
        }}
      >
        <FullscreenIcon fontSize='small' />
      </IconButton>
    </Box>
  );
}

function ZoomControlComponent({ onZoomOut }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    if (onZoomOut) {
      map.setZoom(1);
    }
  }, [map, onZoomOut]);

  return null;
}

function InteractiveWorldMapWrapper({ filters, onZoomOut, riskLevels }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '620px',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid #EAECF0',
      }}
    >
      <MapContainer
        center={[20, 0]}
        zoom={2}
        minZoom={2}
        maxZoom={3}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        {/* <TileLayer
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        /> */}
        <CountryLayer
          showLabels={true}
          filters={filters}
          riskLevels={riskLevels || {}}
        />
        <ZoomControl position='bottomright' />
        {/* <ZoomControlComponent onZoomOut={onZoomOut} /> */}
        <MapControls />
      </MapContainer>

      {/* Layers button */}
      {/* <LayersMenu /> */}
    </Box>
  );
}

function LayersMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showRiskColors, setShowRiskColors] = useState(true);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          left: 10,
          zIndex: 1000,
        }}
      >
        <Button
          variant='contained'
          size='small'
          startIcon={<LayersIcon />}
          onClick={handleClick}
          sx={{
            bgcolor: 'white',
            color: 'text.primary',
            boxShadow: 2,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          Layers
        </Button>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={(e) => e.stopPropagation()}>
          <FormControlLabel
            control={<Checkbox checked={true} disabled size='small' />}
            label='Show Country Names'
            sx={{ m: 0 }}
          />
        </MenuItem>
        <MenuItem onClick={(e) => e.stopPropagation()}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showRiskColors}
                onChange={(e) => setShowRiskColors(e.target.checked)}
                size='small'
              />
            }
            label='Show Risk Colors'
            sx={{ m: 0 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}

function getSeverityColor(severity) {
  switch (severity.toLowerCase()) {
    case 'high':
      return '#ef4444'; // Red
    case 'medium':
      return '#f59e0b'; // Yellow/Orange
    case 'low':
      return '#3b82f6'; // Blue
    default:
      return '#6b7280'; // Gray
  }
}

export default function TravelInsights({ totalTravelingEmployees = 0 }) {
  const [department, setDepartment] = useState('');
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const [summaryStats, setSummaryStats] = useState({
    totalTravelingEmployees: 0,
    countriesCovered: 12,
    highRiskCountries: 3,
    activeAlerts: 5,
  });
  const [dynamicCountryRiskLevels, setDynamicCountryRiskLevels] = useState({});

  // Comprehensive list of all world countries
  const countryOptions = React.useMemo(() => {
    const allCountries = [
      'Afghanistan',
      'Albania',
      'Algeria',
      'Andorra',
      'Angola',
      'Antigua and Barbuda',
      'Argentina',
      'Armenia',
      'Australia',
      'Austria',
      'Azerbaijan',
      'Bahamas',
      'Bahrain',
      'Bangladesh',
      'Barbados',
      'Belarus',
      'Belgium',
      'Belize',
      'Benin',
      'Bhutan',
      'Bolivia',
      'Bosnia and Herzegovina',
      'Botswana',
      'Brazil',
      'Brunei',
      'Bulgaria',
      'Burkina Faso',
      'Burundi',
      'Cambodia',
      'Cameroon',
      'Canada',
      'Cape Verde',
      'Central African Republic',
      'Chad',
      'Chile',
      'China',
      'Colombia',
      'Comoros',
      'Congo',
      'Costa Rica',
      'Croatia',
      'Cuba',
      'Cyprus',
      'Czech Republic',
      'Democratic Republic of the Congo',
      'Denmark',
      'Djibouti',
      'Dominica',
      'Dominican Republic',
      'East Timor',
      'Ecuador',
      'Egypt',
      'El Salvador',
      'Equatorial Guinea',
      'Eritrea',
      'Estonia',
      'Eswatini',
      'Ethiopia',
      'Fiji',
      'Finland',
      'France',
      'Gabon',
      'Gambia',
      'Georgia',
      'Germany',
      'Ghana',
      'Greece',
      'Grenada',
      'Guatemala',
      'Guinea',
      'Guinea-Bissau',
      'Guyana',
      'Haiti',
      'Honduras',
      'Hungary',
      'Iceland',
      'India',
      'Indonesia',
      'Iran',
      'Iraq',
      'Ireland',
      'Israel',
      'Italy',
      'Ivory Coast',
      'Jamaica',
      'Japan',
      'Jordan',
      'Kazakhstan',
      'Kenya',
      'Kiribati',
      'Kosovo',
      'Kuwait',
      'Kyrgyzstan',
      'Laos',
      'Latvia',
      'Lebanon',
      'Lesotho',
      'Liberia',
      'Libya',
      'Liechtenstein',
      'Lithuania',
      'Luxembourg',
      'Madagascar',
      'Malawi',
      'Malaysia',
      'Maldives',
      'Mali',
      'Malta',
      'Marshall Islands',
      'Mauritania',
      'Mauritius',
      'Mexico',
      'Micronesia',
      'Moldova',
      'Monaco',
      'Mongolia',
      'Montenegro',
      'Morocco',
      'Mozambique',
      'Myanmar',
      'Namibia',
      'Nauru',
      'Nepal',
      'Netherlands',
      'New Zealand',
      'Nicaragua',
      'Niger',
      'Nigeria',
      'North Korea',
      'North Macedonia',
      'Norway',
      'Oman',
      'Pakistan',
      'Palau',
      'Palestine',
      'Panama',
      'Papua New Guinea',
      'Paraguay',
      'Peru',
      'Philippines',
      'Poland',
      'Portugal',
      'Qatar',
      'Romania',
      'Russia',
      'Rwanda',
      'Saint Kitts and Nevis',
      'Saint Lucia',
      'Saint Vincent and the Grenadines',
      'Samoa',
      'San Marino',
      'Sao Tome and Principe',
      'Saudi Arabia',
      'Senegal',
      'Serbia',
      'Seychelles',
      'Sierra Leone',
      'Singapore',
      'Slovakia',
      'Slovenia',
      'Solomon Islands',
      'Somalia',
      'South Africa',
      'South Korea',
      'South Sudan',
      'Spain',
      'Sri Lanka',
      'Sudan',
      'Suriname',
      'Sweden',
      'Switzerland',
      'Syria',
      'Taiwan',
      'Tajikistan',
      'Tanzania',
      'Thailand',
      'Togo',
      'Tonga',
      'Trinidad and Tobago',
      'Tunisia',
      'Turkey',
      'Turkmenistan',
      'Tuvalu',
      'Uganda',
      'Ukraine',
      'United Arab Emirates',
      'United Kingdom',
      'United States',
      'United States of America',
      'Uruguay',
      'Uzbekistan',
      'Vanuatu',
      'Vatican City',
      'Venezuela',
      'Vietnam',
      'Yemen',
      'Zambia',
      'Zimbabwe',
    ];
    return allCountries.sort((a, b) => a.localeCompare(b));
  }, []);

  // Fetch State Department Travel Advisory API data on component mount
  useEffect(() => {
    const fetchTravelAdvisoryData = async () => {
      setLoading(true);
      try {
        // Fetch all travel advisories from State Department API
        const response = await stateTravelAdvisoryApi.getAllAdvisories(1, 250);

        if (response && response.data && response.data.length > 0) {
          const allAlerts = [];

          // Map State Department advisories to our format
          response.data.forEach((advisory) => {
            // Skip if country code is invalid (XX)
            if (advisory.countryCode && advisory.countryCode !== 'XX') {
              allAlerts.push({
                country: advisory.country,
                riskType: 'Travel Advisory', // State Department advisories are general travel warnings
                severity: mapStateAdvisoryLevel(advisory.levelNumber),
                title: advisory.title,
                summary: advisory.summary || advisory.level,
                level: advisory.level,
                levelNumber: advisory.levelNumber,
                link: advisory.link,
                lastUpdated: advisory.lastUpdated,
              });
            }
          });

          // Update risk alerts - show high and medium risk countries first
          const sortedAlerts = allAlerts.sort((a, b) => {
            // Sort by severity: High > Medium > Low
            const severityOrder = { High: 3, Medium: 2, Low: 1 };
            return (
              (severityOrder[b.severity] || 0) -
              (severityOrder[a.severity] || 0)
            );
          });

          if (sortedAlerts.length > 0) {
            setRiskAlerts(sortedAlerts);
          }

          // Calculate summary statistics (travel advisory–based)
          const highRiskCount = sortedAlerts.filter(
            (a) => a.severity === 'High',
          ).length;
          const uniqueCountries = new Set(sortedAlerts.map((a) => a.country));

          setSummaryStats((prev) => ({
            ...prev,
            countriesCovered: uniqueCountries.size || 12,
            highRiskCountries: highRiskCount || 3,
            activeAlerts: sortedAlerts.length || 5,
          }));

          // Build country risk levels map for map coloring
          const riskMap = { ...countryRiskLevels };
          sortedAlerts.forEach((alert) => {
            const riskLevel = mapStateAdvisoryToRiskLevel(alert.levelNumber);
            riskMap[alert.country] = riskLevel;
          });
          setDynamicCountryRiskLevels(riskMap);
        }
      } catch (error) {
        console.error(
          'Error fetching State Department travel advisory data:',
          error,
        );
        // Keep default data on error
      } finally {
        setLoading(false);
      }
    };

    fetchTravelAdvisoryData();
  }, []);

  // Filter object for map
  const filters = {
    department,
    country: country || searchQuery,
    status,
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'department') {
      setDepartment(value);
    } else if (filterType === 'country') {
      setCountry(value);
    } else if (filterType === 'status') {
      setStatus(value);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h5' sx={{ fontWeight: 700, color: '#101828' }}>
          Travel &amp; Risk Insights
        </Typography>
        <Typography variant='body2' sx={{ color: '#667085', mt: 0.5 }}>
          Live view of where your workforce is travelling and the risk at each destination.
        </Typography>
      </Box>

      <Grid container spacing={3}>

        {/* Summary stat widgets */}
        {[
          { icon: <PeopleIcon />, label: 'Traveling Employees', value: totalTravelingEmployees, color: '#3b82f6' },
          { icon: <PublicIcon />, label: 'Countries Covered', value: summaryStats.countriesCovered, color: '#10b981' },
          { icon: <WarningIcon />, label: 'High-Risk Countries', value: summaryStats.highRiskCountries, color: '#ef4444' },
          { icon: <NotificationsIcon />, label: 'Active Alerts', value: summaryStats.activeAlerts, color: '#f59e0b' },
        ].map(({ icon, label, value, color }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: 'white', height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
              </Box>
              <Typography variant='body2' sx={{ color: '#6b7280', fontWeight: 500 }}>{label}</Typography>
              <Typography variant='h4' sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>
                {loading ? <CircularProgress size={24} /> : value}
              </Typography>
            </Paper>
          </Grid>
        ))}

        {/* Interactive World Map / List view widget */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid #EAECF0', bgcolor: 'white', overflow: 'hidden' }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 2.5,
                flexWrap: 'wrap',
                borderBottom: '1px solid #F2F4F7',
              }}
            >
              {/* Title */}
              <Box>
                <Typography variant='h6' sx={{ fontWeight: 700, color: '#101828' }}>
                  {viewMode === 'map' ? 'Global Risk Map' : 'Country Risk List'}
                </Typography>
                <Typography variant='body2' sx={{ color: '#667085' }}>
                  {viewMode === 'map'
                    ? 'Employee locations shaded by destination risk level'
                    : 'All countries sorted by advisory severity'}
                </Typography>
              </Box>

              {/* Right side: legend (map only) + view toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {viewMode === 'map' && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                      { c: '#ef4444', l: 'High risk' },
                      { c: '#f59e0b', l: 'Medium risk' },
                      { c: '#10b981', l: 'Low risk' },
                    ].map((x) => (
                      <Box key={x.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: x.c }} />
                        <Typography variant='caption' sx={{ color: '#475467', fontWeight: 600 }}>
                          {x.l}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Map / List toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                  }}
                >
                  <Tooltip title='Map view'>
                    <IconButton
                      size='small'
                      onClick={() => setViewMode('map')}
                      sx={{
                        borderRadius: 0,
                        px: 1.5,
                        bgcolor: viewMode === 'map' ? '#2563EB' : 'white',
                        color: viewMode === 'map' ? 'white' : '#6B7280',
                        '&:hover': {
                          bgcolor: viewMode === 'map' ? '#1D4ED8' : '#F3F4F6',
                        },
                      }}
                    >
                      <MapOutlinedIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='List view'>
                    <IconButton
                      size='small'
                      onClick={() => setViewMode('list')}
                      sx={{
                        borderRadius: 0,
                        px: 1.5,
                        bgcolor: viewMode === 'list' ? '#2563EB' : 'white',
                        color: viewMode === 'list' ? 'white' : '#6B7280',
                        '&:hover': {
                          bgcolor: viewMode === 'list' ? '#1D4ED8' : '#F3F4F6',
                        },
                      }}
                    >
                      <ViewListIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Content — map or list */}
            {viewMode === 'map' ? (
              <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                <InteractiveWorldMapWrapper filters={filters} onZoomOut={false} riskLevels={dynamicCountryRiskLevels} />
              </Box>
            ) : (
              <RiskListView
                riskAlerts={riskAlerts}
                loading={loading}
                country={country || searchQuery}
                status={status}
              />
            )}
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}

// ── List view component ────────────────────────────────────────────────────────

function severityColor(severity) {
  switch ((severity || '').toLowerCase()) {
    case 'high':   return { bg: '#fee2e2', text: '#991b1b', dot: '#dc2626' };
    case 'medium': return { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' };
    case 'low':    return { bg: '#d1fae5', text: '#065f46', dot: '#10b981' };
    default:       return { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' };
  }
}

function RiskListView({ riskAlerts, loading, country, status }) {
  const filtered = riskAlerts.filter((alert) => {
    const matchCountry = !country || alert.country.toLowerCase().includes(country.toLowerCase());
    const matchStatus  = !status  || alert.severity === status;
    return matchCountry && matchStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (filtered.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <PublicIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
        <Typography variant='body1' color='text.secondary'>No countries match the current filter.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 620 }}>
      <Table stickyHeader size='small'>
        <TableHead>
          <TableRow>
            {['Country', 'Risk Level', 'Advisory', 'Summary', 'Updated', ''].map((col) => (
              <TableCell
                key={col}
                sx={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: '#374151',
                  bgcolor: '#F9FAFB',
                  borderBottom: '1px solid #E5E7EB',
                  whiteSpace: 'nowrap',
                  py: 1.5,
                  px: 2,
                }}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((alert, idx) => {
            const sc = severityColor(alert.severity);
            return (
              <TableRow
                key={`${alert.country}-${idx}`}
                hover
                sx={{ '&:last-child td': { border: 0 } }}
              >
                {/* Country */}
                <TableCell sx={{ fontWeight: 600, color: '#111827', py: 1.5, px: 2, whiteSpace: 'nowrap' }}>
                  {alert.country}
                </TableCell>

                {/* Risk chip */}
                <TableCell sx={{ py: 1.5, px: 2, whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sc.dot, flexShrink: 0 }} />
                    <Typography
                      variant='caption'
                      sx={{
                        fontWeight: 700,
                        color: sc.text,
                        bgcolor: sc.bg,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {alert.severity}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Advisory level text */}
                <TableCell sx={{ color: '#374151', fontSize: 13, py: 1.5, px: 2, maxWidth: 200 }}>
                  {alert.level || alert.riskType}
                </TableCell>

                {/* Summary */}
                <TableCell
                  sx={{
                    color: '#6B7280',
                    fontSize: 13,
                    py: 1.5,
                    px: 2,
                    maxWidth: 340,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={alert.summary || alert.title || ''} placement='top-start'>
                    <span>{alert.summary || alert.title || '—'}</span>
                  </Tooltip>
                </TableCell>

                {/* Last updated */}
                <TableCell sx={{ color: '#9CA3AF', fontSize: 12, py: 1.5, px: 2, whiteSpace: 'nowrap' }}>
                  {alert.lastUpdated
                    ? new Date(alert.lastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—'}
                </TableCell>

                {/* Link */}
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  {alert.link && (
                    <Tooltip title='View full advisory'>
                      <IconButton
                        size='small'
                        component='a'
                        href={alert.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{ color: '#2563EB' }}
                      >
                        <OpenInNewIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
